import { useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { SheetModal } from "./SheetModal";
import { copyToClipboard } from "../lib/platform/clipboard";
import { useToast } from "./ux/Toast";
import type { PublicEventResponse } from "../lib/api/useEventPublic";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  event: PublicEventResponse;
}

export function ShareEventModal({ isOpen, onClose, event }: Props) {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const shareUrl =
    typeof window !== "undefined" && event.slug
      ? `${window.location.origin}/events/${event.slug}`
      : "";

  const handleCopy = async () => {
    if (!shareUrl) return;
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-sm">
      <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <h2 className="text-lg font-semibold flex-1">Share event</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-5 py-5">
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
          Copy the link to share this event.
        </p>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!shareUrl}
          className="w-full flex items-center gap-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 px-3 py-2.5 text-left transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50"
        >
          <span className="flex-1 truncate text-sm text-neutral-600 dark:text-neutral-300">
            {shareUrl.replace(/^https?:\/\//, "")}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 shrink-0">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </span>
        </button>
      </div>
    </SheetModal>
  );
}
